/**
 * NY Vanilla — Premium Header & Drawer Controller
 */
(function () {
  'use strict';

  const SELECTORS = {
    header: '.header-premium',
    menuToggle: '.header-premium__menu-toggle',
    drawer: '.drawer-premium',
    backdrop: '.drawer-premium-backdrop',
    drawerClose: '.drawer-premium__close',
    searchToggle: '.header-premium__search-toggle',
    searchModal: '.search-premium',
    searchClose: '.search-premium__close',
    searchInput: '.search-premium__input',
    parentItem: '[data-has-children]',
  };

  let lastScrollY = 0;
  let ticking = false;
  let isDrawerOpen = false;

  function init() {
    setupStickyHeader();
    setupDrawer();
    setupSearch();
    setupSubmenuToggles();
    setupKeyboardNav();
  }

  function setupStickyHeader() {
    const header = document.querySelector(SELECTORS.header);
    if (!header) return;

    const isFloating = header.classList.contains('header-premium--floating');
    const transparentEnabled = header.classList.contains('header-premium--transparent-enabled');

    if (isFloating) {
      header.style.position = 'fixed';
    }

    // Decide how long the header stays transparent before it flips to the
    // solid/selected menu.
    //   - Home page: transparent over the whole hero (first section), flipping
    //     off just as the bottom of the hero reaches the header.
    //   - Every other page: flip to the solid menu as soon as the user scrolls
    //     down once.
    const mainContent = document.getElementById('MainContent');
    const isHome = mainContent && mainContent.getAttribute('data-template') === 'index';

    function computeTransparentThreshold() {
      if (!transparentEnabled) return 0;
      if (!isHome) return 1; // non-home: any downward scroll switches to solid
      const firstSection = mainContent && mainContent.querySelector(':scope > .shopify-section');
      if (firstSection) {
        const headerHeight = header.offsetHeight || 80;
        return Math.max(firstSection.offsetHeight - headerHeight, 40);
      }
      return 160; // fallback if the first section can't be measured
    }

    let transparentThreshold = computeTransparentThreshold();

    // Recompute when the layout changes (responsive height shifts, late media).
    window.addEventListener('resize', function () {
      transparentThreshold = computeTransparentThreshold();
    }, { passive: true });
    window.addEventListener('load', function () {
      transparentThreshold = computeTransparentThreshold();
    });

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const delta = currentScrollY - lastScrollY;

          header.classList.toggle('header-premium--scrolled', currentScrollY > 4);

          if (transparentEnabled) {
            header.classList.toggle(
              'header-premium--transparent-top',
              currentScrollY < transparentThreshold
            );
          }

          // Hide on scroll down, reveal on scroll up — but only after a real
          // movement (deadzone). Tiny scrolls, momentum, and iOS rubber-banding
          // no longer flip the header in and out, so it feels intentional rather
          // than glitchy. Never hide while the drawer is open or near the top.
          if (!isDrawerOpen) {
            if (currentScrollY <= 80) {
              header.classList.remove('header-premium--hidden');
            } else if (delta > 6) {
              header.classList.add('header-premium--hidden');
            } else if (delta < -6) {
              header.classList.remove('header-premium--hidden');
            }
          }

          lastScrollY = currentScrollY;
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function setupDrawer() {
    const toggles = document.querySelectorAll(SELECTORS.menuToggle);
    const drawer = document.querySelector(SELECTORS.drawer);
    const backdrop = document.querySelector(SELECTORS.backdrop);
    const close = document.querySelector(SELECTORS.drawerClose);

    if (!toggles.length || !drawer) return;

    var lastTrigger = null;

    function setAllToggles(expanded) {
      toggles.forEach(function (t) {
        t.setAttribute('aria-expanded', expanded);
      });
    }

    function openDrawer() {
      isDrawerOpen = true;
      drawer.setAttribute('data-open', 'true');
      setAllToggles('true');
      if (backdrop) backdrop.setAttribute('data-visible', 'true');
      document.body.classList.add('drawer-open');

      var header = document.querySelector(SELECTORS.header);
      if (header) header.classList.remove('header-premium--hidden');

      // Move focus into the drawer once it has finished sliding in. preventScroll
      // stops the page from lurching when focus lands inside the fixed panel.
      var firstLink = drawer.querySelector('.drawer-premium__menu-link');
      if (firstLink) {
        setTimeout(function () {
          try { firstLink.focus({ preventScroll: true }); }
          catch (e) { firstLink.focus(); }
        }, 360);
      }
    }

    function closeDrawer() {
      if (!isDrawerOpen) return;
      isDrawerOpen = false;
      drawer.setAttribute('data-open', 'false');
      setAllToggles('false');
      if (backdrop) backdrop.setAttribute('data-visible', 'false');
      document.body.classList.remove('drawer-open');

      drawer.querySelectorAll('.drawer-premium__menu-item--open').forEach(function (item) {
        item.classList.remove('drawer-premium__menu-item--open');
      });

      // Return focus to the control that opened the menu (accessibility + feels
      // deliberate). Skip if the menu was closed by following a link.
      if (lastTrigger && document.contains(lastTrigger)) {
        try { lastTrigger.focus({ preventScroll: true }); }
        catch (e) { /* no-op */ }
      }
    }

    toggles.forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        if (isDrawerOpen) {
          closeDrawer();
        } else {
          lastTrigger = this;
          openDrawer();
        }
      });
    });

    if (close) close.addEventListener('click', closeDrawer);
    if (backdrop) backdrop.addEventListener('click', closeDrawer);

    // Tapping an actual destination link (leaf nav item or submenu item) closes
    // the drawer. Parent items only toggle their submenu, so they're excluded.
    drawer
      .querySelectorAll('.drawer-premium__menu-link:not(.drawer-premium__menu-link--parent), .drawer-premium__submenu-link')
      .forEach(function (link) {
        link.addEventListener('click', function () { closeDrawer(); });
      });

    window._nvCloseDrawer = closeDrawer;
  }

  function setupSubmenuToggles() {
    var parentItems = document.querySelectorAll(SELECTORS.parentItem);

    parentItems.forEach(function (item) {
      var link = item.querySelector('.drawer-premium__menu-link--parent');
      if (!link) return;

      link.addEventListener('click', function (e) {
        e.preventDefault();
        var wasOpen = item.classList.contains('drawer-premium__menu-item--open');

        item.parentElement.querySelectorAll('.drawer-premium__menu-item--open').forEach(function (sibling) {
          sibling.classList.remove('drawer-premium__menu-item--open');
        });

        if (!wasOpen) {
          item.classList.add('drawer-premium__menu-item--open');
        }
      });
    });
  }

  function setupSearch() {
    var toggle = document.querySelector(SELECTORS.searchToggle);
    var modal = document.querySelector(SELECTORS.searchModal);
    var close = document.querySelector(SELECTORS.searchClose);
    var input = document.querySelector(SELECTORS.searchInput);

    if (!toggle || !modal) return;

    toggle.addEventListener('click', function () {
      modal.setAttribute('data-open', 'true');
      document.body.classList.add('drawer-open');
      if (input) setTimeout(function () { input.focus(); }, 300);
    });

    function closeSearch() {
      modal.setAttribute('data-open', 'false');
      document.body.classList.remove('drawer-open');
    }

    if (close) close.addEventListener('click', closeSearch);

    modal.addEventListener('click', function (e) {
      if (e.target === modal) closeSearch();
    });
  }

  function setupKeyboardNav() {
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        var searchModal = document.querySelector(SELECTORS.searchModal);
        if (searchModal && searchModal.getAttribute('data-open') === 'true') {
          searchModal.setAttribute('data-open', 'false');
          document.body.classList.remove('drawer-open');
          var st = document.querySelector(SELECTORS.searchToggle);
          if (st) st.focus();
          return;
        }

        if (isDrawerOpen && window._nvCloseDrawer) {
          window._nvCloseDrawer();
        }
      }
    });

    var drawer = document.querySelector(SELECTORS.drawer);
    if (!drawer) return;

    drawer.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      if (drawer.getAttribute('data-open') !== 'true') return;

      var focusable = drawer.querySelectorAll(
        'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
      );
      if (focusable.length === 0) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();